import React, { useState } from 'react';
import { Video, Phone, Clock, Calendar, Wifi, Lock, MessageCircle, ClipboardCheck, Stethoscope, GraduationCap, Heart, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import './LocationBranch.css';
import './LocationTelehealth.css';

const LocationTelehealth = () => {
  const [expandedService, setExpandedService] = useState(null);
  
  const toggleService = (index) => {
    setExpandedService(expandedService === index ? null : index);
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.clinicalminds.net"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Locations",
        "item": "https://www.clinicalminds.net/locations"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Telehealth",
        "item": "https://www.clinicalminds.net/locations/telehealth"
      }
    ]
  };

  const services = [
    {
      icon: <MessageCircle size={32} strokeWidth={1.5} />,
      title: 'Individual and Group Therapy',
      description: 'Personalised one-on-one or group therapy sessions tailored to your unique needs and goals in a safe, supportive environment.'
    },
    {
      icon: <ClipboardCheck size={32} strokeWidth={1.5} />,
      title: 'Psychological Assessments',
      description: 'Comprehensive evaluations to inform treatment planning or for other purposes such as school support, NDIS or other government support.'
    },
    {
      icon: <Stethoscope size={32} strokeWidth={1.5} />,
      title: 'Diagnosis & Treatment Planning',
      description: 'Evidence based diagnostic assessments and comprehensive treatment planning. I work collaboratively with you to understand your needs and develop effective, evidence-based treatment approaches.'
    },
    {
      icon: <GraduationCap size={32} strokeWidth={1.5} />,
      title: 'School Psychology',
      description: 'Dedicated support for learning difficulties, academic challenges, and educational planning. I work with students, families, and schools to create supportive learning environments.'
    },
    {
      icon: <Heart size={32} strokeWidth={1.5} />,
      title: 'NDIS Support',
      description: 'Comprehensive psychological services for NDIS participants. I provide assessments, and capacity building to help you achieve your goals and live independently.'
    }
  ];

  return (
    <div className="page-location location-telehealth">
      <SEO 
        title="Telehealth Psychology Australia-Wide | Online Psychologist | Clinical Minds"
        description="Telehealth psychology services across Australia. Video consultations with Lachlan Beaton - Clinical Psychologist (Registrar). Same quality as in-person therapy from your home. Medicare rebates available."
        canonicalUrl="https://www.clinicalminds.net/locations/telehealth"
        schemaData={breadcrumbSchema}
      />
      
      {/* Scroll Indicator for Mobile */}
      <ScrollIndicator />
      
      {/* Hero Section */}
      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">Location</span>
          <h1 className="heading-hero">Telehealth</h1>
          <p className="hero-subline">Professional psychology services from the comfort and privacy of your own space</p>
        </div>
      </section>

      <section className="location-details">
        <div className="container">
          <div className="details-grid">
            <div className="detail-main">
              <div className="card detail-card">
                <h2 className="heading-2">Session Information</h2>
                <div className="info-items">
                  <div className="info-item">
                    <Video size={32} strokeWidth={1.5} />
                    <div>
                      <h3 className="heading-3">Secure Platform</h3>
                      <p className="body-medium">
                        All sessions are conducted via an encrypted video platform compliant with Australian privacy law.
                      </p>
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <Clock size={32} strokeWidth={1.5} />
                    <div>
                      <h3 className="heading-3">Availability</h3>
                      <p className="body-medium">
                        Monday, Tuesday, Wednesday: 7:30am - 5:00pm
                      </p>
                      <p className="body-small" className="mt-1">
                        Early and late appointments by request
                      </p>
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <Wifi size={32} strokeWidth={1.5} />
                    <div>
                      <h3 className="heading-3">Technical Requirements</h3>
                      <p className="body-medium">
                        Stable internet connection, webcam, and microphone required
                      </p>
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <Lock size={32} strokeWidth={1.5} />
                    <div>
                      <h3 className="heading-3">Privacy</h3>
                      <p className="body-medium">
                        Find a quiet, private space where you won't be interrupted
                      </p>
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <Phone size={32} strokeWidth={1.5} />
                    <div>
                      <h3 className="heading-3">Contact</h3>
                      <p className="body-small">
                        Phone: <a href="tel:0266002710">02 6600 2710</a><br />
                        SMS: <a href="sms:0415220944">0415 220 944</a><br />
                        Fax: <a href="tel:0266991457">02 6699 1457</a><br />
                        Email: <a href="mailto:contact@clinicalminds.net">contact@clinicalminds.net</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="card services-card">
                <h2 className="heading-2">Telehealth Services</h2>
                <p className="body-medium" className="mb-2">
                  Access the same high-quality psychological care from anywhere in Australia. Our telehealth services are ideal for those who prefer online sessions, have mobility challenges, or live in remote areas.
                </p>
                <div className="services-list">
                  {services.map((service, index) => (
                    <div key={index} className="service-item-expandable">
                      <div 
                        className="service-item clickable"
                        onClick={() => toggleService(index)}
                      >
                        <div className="service-icon-small">{service.icon}</div>
                        <h3 className="heading-3">{service.title}</h3>
                        <div className="expand-icon">
                          {expandedService === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                      {expandedService === index && (
                        <div className="service-description">
                          <p className="body-medium">{service.description}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="detail-sidebar">
              <div className="card cta-card cta-card-enhanced">
                <Calendar size={40} className="cta-icon" />
                <h3 className="heading-2">Request an Appointment</h3>
                <p className="body-medium">
                  Tell us what you need and we'll be in touch within 24 hours.
                </p>
                <div className="cta-benefits">
                  <div className="cta-benefit-item">✓ 24-hour response</div>
                  <div className="cta-benefit-item">✓ Medicare rebates available</div>
                </div>
                <Link to="/bookings" className="btn-primary full-width btn-cta-large">Send a Booking Request</Link>
              </div>
              
              <div className="card info-card">
                <h3 className="heading-3">Medicare & NDIS</h3>
                <p className="body-medium">
                  Telehealth sessions are eligible for Medicare rebates with a valid Mental Health Treatment Plan. NDIS funding also accepted.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CrisisSupport />
    </div>
  );
};

export default LocationTelehealth;
